import { incApi } from 'api/inc-api';
import {
  AddRuleRequest,
  AddRuleResponse,
  ChangeCategoryRequest,
  ChangeCategoryResult,
  DeleteRuleRequest,
  DeleteRuleResponse,
  GetConfigurationResponse,
  GetItemPatternResult,
  GetMLStoresResponse,
  GetModelStatusResponse,
  GetRulesResponse,
  ListItemDetailsResult,
  ListMainItemsTransformedResult,
  ListSelectableItemsResponse,
  UpdateConfigurationRequest,
  UpdateConfigurationResponse,
  UpdateStoreConfigurationRequest,
  UpdateStoreConfigurationResponse,
} from './ml-service.types';
import { getClientId } from 'util/client-id';

const domain = 'ml/portal';

const endpoints = incApi.injectEndpoints({
  overrideExisting: true,
  endpoints: build => ({
    listMainItems: build.query<ListMainItemsTransformedResult, null>({
      query: () => ({
        url: `${domain}/client/mainitem/names/${getClientId()}`,
      }),
      transformResponse: result => ({
        ...result,
        mainItemNames: result.mainItemNames.map(itemName => ({
          id: itemName,
          itemName: itemName,
        })),
      }),
    }),

    listItemDetails: build.query<ListItemDetailsResult, null>({
      query: () => ({
        url: `${domain}/client/item/name/details/${getClientId()}`,
      }),
      providesTags: ['MLItemDetails'],
    }),

    getItemPattern: build.query<GetItemPatternResult, string>({
      query: itemName => ({
        url: `${domain}/pattern`,
        method: 'POST',
        body: {
          clientId: getClientId(),
          itemName,
        },
      }),
    }),

    getRules: build.query<GetRulesResponse, null>({
      query: () => ({
        url: `${domain}/client/rules/${getClientId()}`,
      }),
      providesTags: ['MLRules'],
    }),

    listSelectableItems: build.query<ListSelectableItemsResponse, null>({
      query: () => ({
        url: `${domain}/client/names/${getClientId()}`,
      }),
    }),

    createRule: build.mutation<AddRuleResponse, AddRuleRequest>({
      query: body => ({
        url: `${domain}/client/rules`,
        method: 'POST',
        body: { ...body, clientId: getClientId() },
      }),
      invalidatesTags: ['MLRules'],
    }),

    deleteRule: build.mutation<DeleteRuleResponse, DeleteRuleRequest>({
      query: body => ({
        url: `${domain}/client/rules`,
        method: 'PUT',
        body: { ...body, clientId: getClientId() },
      }),
      invalidatesTags: ['MLRules'],
    }),

    changeCategory: build.mutation<ChangeCategoryResult, ChangeCategoryRequest>(
      {
        query: body => ({
          url: `${domain}/client/item/category/change`,
          method: 'PUT',
          body: { ...body, clientId: getClientId() },
        }),
        invalidatesTags: ['MLItemDetails'],
      },
    ),

    getModelStatus: build.query<GetModelStatusResponse, null>({
      query: () => ({
        url: `${domain}/client/model/status/${getClientId()}`,
      }),
      providesTags: ['MLModelStatus'],
    }),

    getConfiguration: build.query<GetConfigurationResponse, null>({
      query: () => ({
        url: `${domain}/client/configuration/${getClientId()}`,
      }),
      providesTags: ['MLConfiguration'],
    }),

    submitConfiguration: build.mutation<
      UpdateConfigurationResponse,
      UpdateConfigurationRequest
    >({
      query: body => ({
        url: `${domain}/client/configuration`,
        method: 'PUT',
        body: { ...body, clientId: getClientId() },
      }),
      invalidatesTags: ['MLConfiguration'],
    }),

    getMLStores: build.query<GetMLStoresResponse, null>({
      query: () => ({
        url: `${domain}/client/location/${getClientId()}`,
      }),
      providesTags: ['MLStores'],
    }),

    updateStoreConfiguration: build.mutation<
      UpdateStoreConfigurationResponse,
      UpdateStoreConfigurationRequest
    >({
      query: body => ({
        url: `${domain}/client/location/${getClientId()}`,
        method: 'PUT',
        body: { ...body, clientId: getClientId() },
      }),
      invalidatesTags: ['MLStores'],
    }),
  }),
});

export const {
  useListMainItemsQuery,
  useGetItemPatternQuery,
  useListItemDetailsQuery,
  useGetRulesQuery,
  useCreateRuleMutation,
  useListSelectableItemsQuery,
  useChangeCategoryMutation,
  useDeleteRuleMutation,
  useGetModelStatusQuery,
  useGetConfigurationQuery,
  useSubmitConfigurationMutation,
  useGetMLStoresQuery,
  useUpdateStoreConfigurationMutation,
} = endpoints;
